/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toMoney } from "src/utils/formatter";
import FilterData from "src/components/flujocaja/areaDetail/filterData/filterData";
import LabelData from "src/components/flujocaja/areaDetail/label";
import TableDetail from "src/components/flujocaja/areaDetail/tableDetail";
import InnerCard from "src/components/flujocaja/areaDetail/cardDetail/innerCard"
import { Dropdown, Icon } from "semantic-ui-react"
import { GetFieldDetail } from "src/graphql/cashflow";
import { ActionByOption, GetDropdownOptions } from "src/components/flujocaja/areaDetail/rowDetail/functions";
import "./style.scss";
import { PayoffModal } from "../modal/flow/payoff";
import { PartialPayoffModal } from "../modal/flow/partialPayoff";
import { PostponeModal } from "../modal/flow/postpone";
import { RevertPayoff } from "../modal/flow/revertPayoff";
import { ChequePayoff } from "../modal/flow/chequePayoff";
import { importOpenSidebar } from "src/redux/actions/openSidebar";
import { EditAreaModal } from "../modal/flow/editArea";
import { banksFormat } from "src/utils/bankList";
import { BulkActionsBar } from "../widgets/bulkActionsBar/bulkActionsBar";
import CreateNominaFromCashflow from "./modal/nomina/CreateNominaFromCashflow";
import { useBlockedFeatures } from "src/hooks/useBlockedFeatures";

const pointType = {
  TOTAL: 'TOTAL',
  LATE: 'LATE',
  SETTLED: 'SETTLED',
}

export default function FieldData({ editData }) {
  const { t } = useTranslation("finance");
  const dispatch = useDispatch()
  const { abonarBlocked } = useBlockedFeatures()
  const { selectedCompany: { id: selectedCompanyId } } = useSelector((state) => state.companyList)
  const { updateCashflowNeeded } = useSelector((state) => state.flujocaja)
  const effectiveCompanyId = editData?.companyId || selectedCompanyId

  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState([])
  const [totalSelected, setTotalSelected] = useState({ amount: 0 })
  const [type, setType] = useState(editData?.value?.isPast ? pointType.SETTLED : pointType.TOTAL)
  const [selectAll, setSelectAll] = useState(false)
  const { value, cashflowAreaId, isReceived } = editData || {}
  const companyNameById = useMemo(() => {
    const byId = {};
    (editData?.companyBreakdown || []).forEach((item) => {
      if (item?.companyId && item?.companyName) byId[String(item.companyId)] = item.companyName;
    });
    return byId;
  }, [editData?.companyBreakdown]);
  const getPointCompanyId = (point) =>
    point?.companyId || point?.meta?.companyId || point?.document?.companyId || point?.document?.meta?.companyId;
  const getPointCompanyName = (point) => {
    const pointCompanyId = getPointCompanyId(point);
    if (pointCompanyId && companyNameById[String(pointCompanyId)]) return companyNameById[String(pointCompanyId)];
    return point?.companyName || point?.meta?.companyName || point?.document?.companyName || point?.document?.meta?.companyName || t("cashflowAreaDetail.noCompany");
  };
  const { data, fetching, reexecuteFlow } = GetFieldDetail({ companyId: effectiveCompanyId, pointIds: value?.documents || [] })
  const [editDropdownIsOpen, setEditDropdownIsOpen] = useState(false);
  const [settleDropdownIsOpen, setSettleDropdownIsOpen] = useState(false);
  const [nominaModalOpen, setNominaModalOpen] = useState(false);
  const [nominaModalMode, setNominaModalMode] = useState("new"); // "new" o "add"
  const [expandedCompanySections, setExpandedCompanySections] = useState({});

  const {
    lateAmount = 0,
    lateLength = 0,
    settledAmount = 0,
    settledLength = 0,
    totalAmount = 0,
    totalLength = 0,
    points,
    documents,
  } = data?.getFlowAreaDetail || {}

  useEffect(() => {
    reexecuteFlow({ requestPolicy: 'cache-and-network' })
    setTotalSelected({ value: 0 })
    setSearch('')
    setSelected([])
  }, [effectiveCompanyId, cashflowAreaId, value?.documents, updateCashflowNeeded])

  useEffect(() => {
    setSelectAll(false)
  }, [setSelectAll])

  useEffect(() => {
    if (updateCashflowNeeded?.value) reexecuteFlow({ requestPolicy: 'cache-and-network' })
  }, [updateCashflowNeeded?.value])


  const filteredPoints = useMemo(() => {
    if (!documents) return [];


    const selectedPoints = documents.filter((document) => {
      if (type == pointType.TOTAL && !document.meta.isPaid) return true;
      if (type == pointType.SETTLED && document.meta.isPaid) return true;
      if (type == pointType.LATE && document.isLate) return true;

      return false;
    });

    if (!search) return selectedPoints;

    return selectedPoints.filter((document) => {
      const [description] = document?.meta?.detail || document?.meta?.description || []
      const text = description?.description
      const name = isReceived ? document?.meta?.issuerName : document?.meta?.receiverName
      const typeLabel = document?.meta?.typeLabel
      const number = document?.meta?.number || document?.meta?.documentId
      const amount = document?.meta?.amount
      const amountCredit = document?.amount
      const nameBank = banksFormat[document?.name]
      const issuerId = document?.meta?.issuerId;
      const receiverId = document?.meta?.receiverId;
  
      if (name && name.toLowerCase().includes(search.toLowerCase())) return true;
      if (issuerId && issuerId.toLowerCase().includes(search.replace(/\./g, "").toLowerCase())) return true;
      if (receiverId && receiverId.toLowerCase().includes(search.replace(/\./g, "").toLowerCase())) return true;
      if (typeLabel && typeLabel.toLowerCase().includes(search.toLowerCase())) return true;
      if (number && String(number).toLowerCase().includes(search.toLowerCase())) return true;
      if (amount && String(amount).toLowerCase().includes(search.toLowerCase())) return true;
      if (text && String(text).toLowerCase().includes(String(search).toLowerCase())) return true;
      if (amountCredit && String(amountCredit).toLowerCase().includes(search.toLowerCase())) return true;
      if (nameBank && String(nameBank).toLowerCase().includes(search.toLowerCase())) return true;

      return false;
    });
  }, [effectiveCompanyId, cashflowAreaId, search, documents, type]);

  const groupedPointsByCompany = useMemo(() => {
    if (!editData?.isHoldingConsolidated || !Array.isArray(filteredPoints)) return [];
    const companyAmountById = (editData?.companyBreakdown || []).reduce((acc, item) => {
      if (item?.companyId) acc[String(item.companyId)] = item?.value;
      return acc;
    }, {});

    const grouped = filteredPoints.reduce((acc, point) => {
      const companyId = String(getPointCompanyId(point) || "unknown");
      const companyName = getPointCompanyName(point);
      if (!acc[companyId]) {
        acc[companyId] = {
          companyId,
          companyName,
          points: [],
          amount: Number(companyAmountById[companyId] || 0),
        };
      }
      acc[companyId].points.push(point);
      return acc;
    }, {});
    return Object.values(grouped).sort((a, b) => a.companyName.localeCompare(b.companyName));
  }, [editData?.isHoldingConsolidated, filteredPoints, companyNameById, editData?.companyBreakdown]);

  useEffect(() => {
    if (!editData?.isHoldingConsolidated) return;
    setExpandedCompanySections((prev) => {
      const next = { ...prev };
      groupedPointsByCompany.forEach((section) => {
        if (next[section.companyId] === undefined) next[section.companyId] = true;
      });
      return next;
    });
  }, [editData?.isHoldingConsolidated, groupedPointsByCompany]);


  if (!editData?.value) return <div></div>

  const setOption = async (pointType) => {
    await resetPoint()
    setSelectAll(false)
    setTotalSelected({ amount: 0 })
    setSelected([])
    setType(pointType)
  }

  const resetPoint = async () => {
    filteredPoints.map((point) => {
      point.selected = false
    })
  }

  const getDropdownOptions = GetDropdownOptions(
    type == pointType.SETTLED,
    { name: value?.label },
    selected,
    abonarBlocked,
  )

  // Filter out all payoff-related options for the edit dropdown
  const filteredOptions = getDropdownOptions?.filter(option => 
    !['payoff', 'partialPayoff', 'chequePayoff', 'revertPayoff'].includes(option.value)
  )

  // Filter only payoff-related options for the settle dropdown
  const settleOptions = getDropdownOptions?.filter(option =>
    ['payoff', 'partialPayoff', 'chequePayoff', 'revertPayoff'].includes(option.value)
  )

  // Same rule as per-row 3-dots menu: only estimaciones/proyecciones can be settled
  const isProjectionType = (doc) => {
    const projectionTypes = ['proyeccion', 'estimaciondeingreso', 'estimaciondeegreso'];
    return projectionTypes.includes(doc?.meta?.type);
  };
  const allSelectedAreProjections = selected.length > 0 && selected.every(doc => isProjectionType(doc));

  const settleActionsDropdown = <Dropdown
    text={t("cashflowAreaDetail.settle")}
    upward
    defaultValue="payoff"
    disabled={!allSelectedAreProjections || abonarBlocked}
    className={`tw-font-medium tw-w-[92px] tw-px-4 tw-py-1 tw-rounded-lg !tw-text-gray-200 ${!allSelectedAreProjections ? 'tw-bg-gray-500 tw-opacity-50' : settleDropdownIsOpen ? '!tw-bg-gray-500' : 'tw-bg-gray-700'}`}
    onOpen={()=> setSettleDropdownIsOpen(true)}
    onClose={()=> setSettleDropdownIsOpen(false)}
  >
    <Dropdown.Menu>
      {selected?.length > 0
        ? settleOptions?.map((option, index) => {
          if (selected.length > 1 && (option.value === 'partialPayoff' || option.value === 'chequePayoff')) return null

          if (option.value === 'payoff') return <PayoffModal key={`payoff-${index}`} companyId={effectiveCompanyId} option={option} selected={selected} />
          if (option.value === 'partialPayoff') return <PartialPayoffModal key={`partial-${index}`} companyId={effectiveCompanyId} option={option} selected={selected} />
          if (option.value === 'chequePayoff') return <ChequePayoff key={`cheque-${index}`} companyId={effectiveCompanyId} option={option} selected={selected} />
          if (option.value === 'revertPayoff') return <RevertPayoff key={`revert-${index}`} companyId={effectiveCompanyId} option={option} selected={selected} />

          return <div key={`empty-${index}`}></div>
        })
        : []}
    </Dropdown.Menu>
  </Dropdown>;

  const editActionsDropdown = <Dropdown
    text={t("cashflowAreaDetail.edit")}
    className={`tw-font-medium tw-w-[90px] tw-px-4 tw-py-1 tw-rounded-lg !tw-text-gray-200 ${editDropdownIsOpen ? '!tw-bg-gray-500' : 'tw-bg-gray-700'}`}
    upward
    onOpen={()=> setEditDropdownIsOpen(true)}
    onClose={()=> setEditDropdownIsOpen(false)}
  >
    <Dropdown.Menu>
      {selected?.length > 0
        ? filteredOptions?.map((option, index) => {
          if (option.value === 'postpone') return <PostponeModal key={`postpone-${index}`} companyId={effectiveCompanyId} option={option} selected={selected} />

          if (option.value === 'editArea') return <EditAreaModal key={`editArea-${index}`} companyId={effectiveCompanyId} option={option} selected={selected} />

          if (option.value === 'editManual') return (
            <Dropdown.Item
              key={`editManual-${index}`}
              value={option?.value}
              onClick={() => {
                dispatch(
                  importOpenSidebar({
                    show: true,
                    createObligation: true,
                    editData: selected[0],
                    source: 'editObligation'
                  })
                )
              }
              }
            > {option?.text}
            </Dropdown.Item>
          )

          return <div key={`empty-edit-${index}`}></div>
        })
        : []}
    </Dropdown.Menu>
  </Dropdown>

  // Función para crear dropdown de nómina
  const createNominaDropdown = () => {
    // Validación más robusta de documentos
    const hasValidDocuments = selected.length > 0 && 
      selected.some(doc => {
        // Verificar que el documento no esté pagado
        const isNotPaid = !doc.meta?.isPaid;
        
        // Verificar que el documento sea recibido
        const isReceived = doc.meta?.isReceived === true;
        
        // Verificar que tenga los campos mínimos necesarios
        const hasRequiredFields = doc.documentType === 'OBLIGATION' || 
          doc.meta?.documentCode || 
          doc.meta?.issuerId ||
          doc.meta?.receiverId;
        
        // Verificar que tenga un monto válido
        const hasValidAmount = (doc.meta?.remaining || doc.amount) > 0;
        
        // Verificar que el documento no esté 100% en nómina
        const nominaStatus = doc.meta?.nominaStatus || {};
        const remaining = doc.meta?.remaining || doc.amount || 0;
        const totalInNomina = (nominaStatus.totalNomina || 0) + 
                              (nominaStatus.totalSelected || 0) + 
                              (nominaStatus.totalApproved || 0);
        const selectableRemaining = remaining - totalInNomina;
        const isNotFullyInNomina = selectableRemaining > 0;
        
        return isNotPaid && isReceived && hasRequiredFields && hasValidAmount && isNotFullyInNomina;
      });

    return (
      <Dropdown
        text={t("cashflowAreaDetail.createPayroll")}
        className={`tw-font-medium tw-px-4 tw-py-1 tw-rounded-lg !tw-text-gray-200 ${hasValidDocuments ? 'tw-bg-gray-700' : 'tw-bg-gray-500 tw-opacity-50'}`}
        upward
        disabled={!hasValidDocuments}
      >
        <Dropdown.Menu>
          <Dropdown.Item 
            disabled={!hasValidDocuments}
            onClick={() => {
              if (!hasValidDocuments) return;
              setNominaModalMode("new");
              setNominaModalOpen(true);
            }}
          >
            <i className="plus circle icon"></i>
            {t("cashflowAreaDetail.createNewPayroll")}
          </Dropdown.Item>
          <Dropdown.Item 
            disabled={!hasValidDocuments}
            onClick={() => {
              if (!hasValidDocuments) return;
              setNominaModalMode("add");
              setNominaModalOpen(true);
            }}
          >
            <i className="add circle icon"></i>
            {t("cashflowAreaDetail.addToExistingPayroll")}
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );
  };
  return (
    <div className="detailContainer">
      <div className="cardContainer">
        <InnerCard
          header={t("cashflowAreaDetail.total")}
          headerColor="#CCF1FF"
          borderColor="#0C9DF3"
          data={{ total: totalAmount, count: totalLength }}
          showIcon="dollar"
          iconColor="blue"
          onClick={() => setOption(pointType.TOTAL)}
          type="regularPoints"
          disabled={type !== pointType.TOTAL}
          opacity={type !== pointType.TOTAL ? 0.45 : 1}
        />
        <InnerCard
          header={t("cashflowAreaDetail.overdue")}
          headerColor="#F9EAAF"
          borderColor="#f7931e"
          data={{ total: lateAmount, count: lateLength }}
          showIcon="exclamation"
          iconColor="orange"
          onClick={() => setOption(pointType.LATE)}
          type="delayedPoints"
          disabled={type !== pointType.LATE}
          opacity={type !== pointType.LATE ? 0.45 : 1}
        />
        <InnerCard
          header={t("cashflowAreaDetail.settled")}
          headerColor="#B0F7B0"
          borderColor="#39b54a"
          data={{ total: settledAmount, count: settledLength }}
          showIcon="check"
          iconColor="green"
          onClick={() => setOption(pointType.SETTLED)}
          type="settledPoints"
          disabled={type !== pointType.SETTLED}
          opacity={type !== pointType.SETTLED ? 0.45 : 1}
        />
      </div>
      <hr style={{ color: "lightgrey" }} />
      <div className="filterTotalOptions">
        <div>
          <FilterData
            label={t("cashflowAreaDetail.search")}
            placeholder={t("cashflowAreaDetail.searchPlaceholder")}
            value={search}
            onChange={setSearch}
          />
        </div>
        <div>
          <LabelData
            label={t("cashflowAreaDetail.totalSelected")}
            data={totalSelected.amount ? toMoney(totalSelected?.amount) : "$ 0"}
          />
        </div>
        {/* <div
          style={{
            display: "flex",
            alignItems: "center",
            marginTop: "15px",
            minWidth: "150px",
            maxWidth: "160px",
          }}
        >
          <Dropdown text="Opciones" fluid selection>
            <Dropdown.Menu>
              {selected?.length > 0
                ? getDropdownOptions?.map((option) => {
                  if (selected.length > 1 && (option.value === 'partialPayoff' || option.value === 'chequePayoff')) return null

                  //"editManual"
                  //"factorize"

                  if (option.value === 'payoff') return <PayoffModal companyId={companyId} option={option} selected={selected} />
                  if (option.value === 'partialPayoff') return <PartialPayoffModal companyId={companyId} option={option} selected={selected} />

                  if (option.value === 'chequePayoff') return <ChequePayoff companyId={companyId} option={option} selected={selected} />
                  if (option.value === 'postpone') return <PostponeModal companyId={companyId} option={option} selected={selected} />
                  if (option.value === 'revertPayoff') return <RevertPayoff companyId={companyId} option={option} selected={selected} />
                  if (option.value === 'editArea') return <EditAreaModal companyId={companyId} option={option} selected={selected} />

                  if (option.value === 'editManual') return (
                    <Dropdown.Item
                      value={option?.value}
                      onClick={() => {
                        dispatch(
                          importOpenSidebar({
                            show: true,
                            createObligation: true,
                            editData: selected[0],
                            source: 'editObligation'
                          })
                        )
                      }
                      }
                    > {option?.text}
                    </Dropdown.Item>
                  )

                  return <div></div>
                })
                : []}
            </Dropdown.Menu>
          </Dropdown>
        </div> */}
      </div>
      {editData?.isHoldingConsolidated ? (
        <div className="containerInnerTableFcDetail">
          {groupedPointsByCompany.map((companySection) => (
            <div key={companySection.companyId} style={{ marginBottom: "22px" }}>
              <div
                onClick={() =>
                  setExpandedCompanySections((prev) => ({
                    ...prev,
                    [companySection.companyId]: !prev[companySection.companyId],
                  }))
                }
                style={{
                  fontWeight: 600,
                  fontSize: "14px",
                  marginBottom: "8px",
                  color: "#334155",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  backgroundColor: "#f8fafc",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Icon
                    name={expandedCompanySections[companySection.companyId] ? "angle down" : "angle right"}
                    style={{ margin: 0 }}
                  />
                  <span>{companySection.companyName}</span>
                </div>
                <span>{toMoney(Math.round(companySection.amount || 0))}</span>
              </div>
              {expandedCompanySections[companySection.companyId] && (
                <TableDetail
                  data={companySection.points}
                  openSidebar={{ isReceived }}
                  loading={fetching}
                  filterPoint={search}
                  selectAll={selectAll}
                  setSelected={setSelected}
                  selected={selected}
                  totalSelected={totalSelected}
                  setTotalSelected={setTotalSelected}
                  setSelectAll={setSelectAll}
                  getDropdownOptions={getDropdownOptions}
                  companyId={companySection.companyId}
                  value={value}
                  type={type}
                  pointType={pointType}
                />
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="containerInnerTableFcDetail">
          <TableDetail
            data={filteredPoints}
            openSidebar={{ isReceived }}
            loading={fetching}
            filterPoint={search}
            selectAll={selectAll}
            setSelected={setSelected}
            selected={selected}
            totalSelected={totalSelected}
            setTotalSelected={setTotalSelected}
            setSelectAll={setSelectAll}
            getDropdownOptions={getDropdownOptions}
            companyId={effectiveCompanyId}
            value={value}
            type={type}
            pointType={pointType}
          />
        </div>
      )}

      {
        (totalSelected?.amount != undefined && totalSelected?.amount != 0)  &&
        <BulkActionsBar
          showInSidebar={true}
          totalAmountSelected={totalSelected?.amount}
          settleActionsDropdown={settleActionsDropdown}
          editActionsDropdown={editActionsDropdown}
          createNominaDropdown={createNominaDropdown()}
          setSelectedDocs={setSelected}
          setTotalSelected={setTotalSelected}
          totalSelected={selected.length}
        />
      }

      {/* Modal para crear nómina */}
      <CreateNominaFromCashflow
        selectedDocuments={selected}
        companyId={effectiveCompanyId}
        open={nominaModalOpen}
        initialMode={nominaModalMode}
        onClose={() => setNominaModalOpen(false)}
      />
    </div>
  );
}
